import { useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { fetchAntiAbuseFlags, type AntiAbuseFlagsPayload } from '@/lib/api';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './AdminList.module.css';

const WINDOW_OPTIONS = [
  { value: 6, label: '6 hours' },
  { value: 24, label: '24 hours' },
  { value: 72, label: '3 days' },
  { value: 168, label: '7 days' },
];

export function AntiAbusePage() {
  useRecentRoute('Anti-abuse');
  const [data, setData] = useState<AntiAbuseFlagsPayload | null>(null);
  const [windowHours, setWindowHours] = useState(24);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try { setData(await fetchAntiAbuseFlags(windowHours)); }
    catch (err) { setError(err instanceof Error ? err.message : 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [windowHours]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><AlertTriangle size={18} /></div>
          <div>
            <h1 className={styles.title}>Anti-abuse</h1>
            <p className={styles.subtitle}>
              {data ? `${data.total} flagged user${data.total === 1 ? '' : 's'} in the last ${data.windowHours}h` : '—'}
            </p>
          </div>
        </div>
        <button type="button" className={styles.ghostBtn} onClick={() => void load()} disabled={loading}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      <div className={styles.filterRow}>
        <select
          className={styles.select}
          value={windowHours}
          onChange={(e) => setWindowHours(Number(e.target.value))}
        >
          {WINDOW_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {loading && <p className={styles.loading}>Loading…</p>}

      {!loading && data && data.flagged.length === 0 && (
        <div className={styles.empty}>
          <p className={styles.emptyTitle}>No flags in this window</p>
          <p>Nothing suspicious — a clean sweep.</p>
        </div>
      )}

      {!loading && data && data.flagged.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>User</th>
                <th>Sessions</th>
                <th>Legit / blocked</th>
                <th>Avg kWh</th>
                <th>Total kWh</th>
                <th>Rank / points</th>
                <th>Flags</th>
              </tr>
            </thead>
            <tbody>
              {data.flagged.map((r) => (
                <tr key={r.userId}>
                  <td>
                    <code className={styles.cardCode}>{r.userId.slice(-8)}</code>
                  </td>
                  <td>{r.sessionCount}</td>
                  <td>
                    {r.legitCount}
                    <span className={styles.userSub}> / {r.blockedCount}</span>
                  </td>
                  <td>{r.avgKwh.toFixed(2)}</td>
                  <td>{r.totalKwh.toFixed(2)}</td>
                  <td>
                    {r.currentRank ?? '—'}
                    {r.totalPoints !== null && (
                      <span className={styles.userSub}> / {r.totalPoints.toLocaleString()}</span>
                    )}
                  </td>
                  <td>
                    {r.flags.map((f) => (
                      <span key={f} className={styles.flagChip}>{f}</span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className={styles.subtitle} style={{ marginTop: 12 }}>
        Flags are informational. Use the driver gift-points tool with
        a negative amount to deduct or the friendships voider for
        abuse-pair responses. Nothing is auto-actioned.
      </p>
    </div>
  );
}
